import { TIndirectTaxTransactionAggregates } from '@commenda/api/indirect-tax/transactions/types';
import { BadgeProps, calculateDateRange } from '@commenda/kakashi';
import { formatNumberWithUniqueCommaPattern } from '@commenda/utils';
import { IndirectTaxRegionHelper } from '../../common/helpers/IndirectTaxRegionHelper';
import {
  TIndirectTaxLineItemTaxLogic,
  TIndirectTaxLineItemTaxLogicHandlers,
  TIndirectTaxLineItemTaxLogicRow,
  TIndirectTaxTaxBasis,
  TIndirectTaxTransaction,
  TIndirectTaxTransactionDetailSummaryConfig,
  TIndirectTaxTransactionDocumentType,
  TIndirectTaxTransactionRegion,
  TIndirectTaxTransactionRegionTabConfig,
  TIndirectTaxTransactionStatus,
  TIndirectTaxTransactionSummaryConfig,
  TIndirectTaxTransactionSupplyType,
  TIndirectTaxTransactionType,
  TIndirectTaxTransactionVatStatus,
} from '../types';

export interface LineItemColumnLabels {
  taxLabel: string;
  totalLabel: string;
}

interface BadgeConfig {
  label: string;
  variant: BadgeProps['variant'];
}

interface SummaryItem {
  label: string;
  value: string;
  currencySymbol?: string;
}

export class IndirectTaxTransactionHelper {
  static readonly DETAIL_SUMMARY_CONFIGS: Record<
    TIndirectTaxTransactionRegion,
    TIndirectTaxTransactionDetailSummaryConfig[]
  > = {
    [TIndirectTaxTransactionRegion.ASIA_PACIFIC]: [],
    [TIndirectTaxTransactionRegion.MIDDLE_EAST]: [],
    [TIndirectTaxTransactionRegion.LATIN_AMERICA]: [],
    [TIndirectTaxTransactionRegion.UNSUPPORTED]: [],
    [TIndirectTaxTransactionRegion.ALL_REGIONS]: [
      { label: 'Amount', key: 'amount', isCurrency: true },
      { label: 'Tax', key: 'taxCollected', isCurrency: true },
      { label: 'Total', key: 'total', isCurrency: true },
    ],
    [TIndirectTaxTransactionRegion.UNITED_STATES]: [
      { label: 'Amount', key: 'amount', isCurrency: true },
      { label: 'Tax amount', key: 'taxCollected', isCurrency: true },
      { label: 'Total', key: 'total', isCurrency: true },
    ],
    [TIndirectTaxTransactionRegion.EUROPEAN_UNION]: [
      { label: 'Subtotal', key: 'taxableAmount', isCurrency: true },
      { label: 'VAT', key: 'vat', isCurrency: true },
      { label: 'Gross amount', key: 'grossAmount', isCurrency: true },
    ],
    [TIndirectTaxTransactionRegion.UNITED_KINGDOM]: [
      { label: 'Subtotal', key: 'taxableAmount', isCurrency: true },
      { label: 'VAT', key: 'vat', isCurrency: true },
      { label: 'Gross amount', key: 'grossAmount', isCurrency: true },
    ],
    [TIndirectTaxTransactionRegion.CANADA]: [
      { label: 'Amount', key: 'amount', isCurrency: true },
      { label: 'Tax amount', key: 'taxCollected', isCurrency: true },
      { label: 'Total', key: 'total', isCurrency: true },
    ],
  };

  static readonly REGION_TABS: TIndirectTaxTransactionRegionTabConfig[] = [
    { value: TIndirectTaxTransactionRegion.ALL_REGIONS, label: 'All regions' },
    {
      value: TIndirectTaxTransactionRegion.UNITED_STATES,
      label: 'United States',
    },
    {
      value: TIndirectTaxTransactionRegion.EUROPEAN_UNION,
      label: 'European Union',
    },
    {
      value: TIndirectTaxTransactionRegion.UNITED_KINGDOM,
      label: 'United Kingdom',
    },
    { value: TIndirectTaxTransactionRegion.CANADA, label: 'Canada' },
  ];

  static readonly SUMMARY_CONFIGS: Record<
    TIndirectTaxTransactionRegion,
    TIndirectTaxTransactionSummaryConfig[]
  > = {
    [TIndirectTaxTransactionRegion.ASIA_PACIFIC]: [],
    [TIndirectTaxTransactionRegion.MIDDLE_EAST]: [],
    [TIndirectTaxTransactionRegion.LATIN_AMERICA]: [],
    [TIndirectTaxTransactionRegion.UNSUPPORTED]: [],
    [TIndirectTaxTransactionRegion.ALL_REGIONS]: [
      { label: 'Gross sales', key: 'grossSales', isCurrency: true },
      { label: 'Gross purchases', key: 'grossPurchases', isCurrency: true },
    ],
    [TIndirectTaxTransactionRegion.UNITED_STATES]: [
      { label: 'Tax collected', key: 'taxCollected', isCurrency: true },
      { label: 'Total amount', key: 'total', isCurrency: true },
    ],
    [TIndirectTaxTransactionRegion.EUROPEAN_UNION]: [
      { label: 'Gross sales', key: 'grossSales', isCurrency: true },
      { label: 'Gross purchases', key: 'grossPurchases', isCurrency: true },
      { label: 'Output VAT', key: 'outputVat', isCurrency: true },
      { label: 'Input VAT', key: 'inputVat', isCurrency: true },
    ],
    [TIndirectTaxTransactionRegion.UNITED_KINGDOM]: [
      { label: 'Gross sales', key: 'grossSales', isCurrency: true },
      { label: 'Gross purchases', key: 'grossPurchases', isCurrency: true },
      { label: 'Output VAT', key: 'outputVat', isCurrency: true },
      { label: 'Input VAT', key: 'inputVat', isCurrency: true },
    ],
    [TIndirectTaxTransactionRegion.CANADA]: [
      { label: 'Tax collected', key: 'taxCollected', isCurrency: true },
      { label: 'Total amount', key: 'total', isCurrency: true },
    ],
  };

  static getTransactionStatusBadgeConfig(
    status: TIndirectTaxTransactionStatus
  ): BadgeConfig {
    const map: Record<TIndirectTaxTransactionStatus, BadgeConfig> = {
      [TIndirectTaxTransactionStatus.UNCOMMITTED]: {
        label: 'Recorded',
        variant: 'gray',
      },
      [TIndirectTaxTransactionStatus.COMMITTED]: {
        label: 'Filed',
        variant: 'success',
      },
      [TIndirectTaxTransactionStatus.LOCKED]: {
        label: 'Locked',
        variant: 'gray',
      },
      [TIndirectTaxTransactionStatus.VOIDED]: {
        label: 'Voided',
        variant: 'error',
      },
    };
    return map[status];
  }

  static getVatStatusBadgeConfig(
    vatStatus: TIndirectTaxTransactionVatStatus
  ): BadgeConfig {
    const map: Record<TIndirectTaxTransactionVatStatus, BadgeConfig> = {
      [TIndirectTaxTransactionVatStatus.VERIFIED]: {
        label: 'Verified',
        variant: 'success',
      },
      [TIndirectTaxTransactionVatStatus.UNVERIFIED]: {
        label: 'Unverified',
        variant: 'warning',
      },
      [TIndirectTaxTransactionVatStatus.PENDING]: {
        label: 'Pending',
        variant: 'gray',
      },
    };
    return map[vatStatus];
  }

  static getTransactionTypeBadgeConfig(
    type: TIndirectTaxTransactionType
  ): BadgeConfig {
    const map: Record<TIndirectTaxTransactionType, BadgeConfig> = {
      [TIndirectTaxTransactionType.SALE]: {
        label: 'Sale',
        variant: 'success',
      },
      [TIndirectTaxTransactionType.REFUND]: {
        label: 'Refund',
        variant: 'warning',
      },
      [TIndirectTaxTransactionType.PURCHASE]: {
        label: 'Purchase',
        variant: 'blue',
      },
      [TIndirectTaxTransactionType.PURCHASE_REFUND]: {
        label: 'Purchase refund',
        variant: 'warning',
      },
    };
    return map[type];
  }

  static getSupplyTypeName(type: TIndirectTaxTransactionSupplyType): string {
    const map: Record<TIndirectTaxTransactionSupplyType, string> = {
      [TIndirectTaxTransactionSupplyType.INTRA_COMMUNITY]: 'Intra-community',
      [TIndirectTaxTransactionSupplyType.DOMESTIC]: 'Domestic',
      [TIndirectTaxTransactionSupplyType.EXPORT]: 'Export',
      [TIndirectTaxTransactionSupplyType.IMPORT]: 'Import',
    };
    return map[type];
  }

  static getDocumentTypeName(
    type: TIndirectTaxTransactionDocumentType
  ): string {
    const map: Record<TIndirectTaxTransactionDocumentType, string> = {
      [TIndirectTaxTransactionDocumentType.SALE]: 'Sale',
      [TIndirectTaxTransactionDocumentType.CREDIT_NOTE]: 'Credit note',
      [TIndirectTaxTransactionDocumentType.DEBIT_NOTE]: 'Debit note',
    };
    return map[type];
  }

  static formatCurrency(
    value: number,
    options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
  ): string {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: options?.minimumFractionDigits ?? 2,
      maximumFractionDigits: options?.maximumFractionDigits ?? 2,
    }).format(value);
  }

  static getCurrencySymbol(currency: string): string {
    const map: Record<string, string> = {
      USD: 'USD',
      EUR: 'EUR',
      GBP: 'GBP',
      CAD: 'CAD',
    };
    return map[currency] ?? currency;
  }

  static computeSummary(
    transactions: Readonly<TIndirectTaxTransaction[]>,
    configs: Readonly<TIndirectTaxTransactionSummaryConfig[]>,
    currency: string
  ): SummaryItem[] {
    return configs.map((config) => {
      const total = transactions.reduce((sum, txn) => {
        // Safe: config.key comes from SUMMARY_CONFIGS which only references valid TIndirectTaxTransaction keys
        const val = txn[config.key as keyof TIndirectTaxTransaction];
        return sum + (typeof val === 'number' ? val : 0);
      }, 0);

      return {
        label: config.label,
        value: IndirectTaxTransactionHelper.formatCurrency(total),
        currencySymbol: config.isCurrency
          ? IndirectTaxTransactionHelper.getCurrencySymbol(currency)
          : undefined,
      };
    });
  }

  static getDefaultCurrencyForRegion(
    region: TIndirectTaxTransactionRegion
  ): string {
    return IndirectTaxRegionHelper.getDefaultCurrencyForRegion(region);
  }

  static getTransactionTypesForRegion(
    region: TIndirectTaxTransactionRegion
  ): TIndirectTaxTransactionType[] {
    const SALES_SIDE = [
      TIndirectTaxTransactionType.SALE,
      TIndirectTaxTransactionType.REFUND,
    ];
    const ALL = [
      ...SALES_SIDE,
      TIndirectTaxTransactionType.PURCHASE,
      TIndirectTaxTransactionType.PURCHASE_REFUND,
    ];
    const map: Record<
      TIndirectTaxTransactionRegion,
      TIndirectTaxTransactionType[]
    > = {
      [TIndirectTaxTransactionRegion.ASIA_PACIFIC]: ALL,
      [TIndirectTaxTransactionRegion.MIDDLE_EAST]: ALL,
      [TIndirectTaxTransactionRegion.LATIN_AMERICA]: ALL,
      [TIndirectTaxTransactionRegion.UNSUPPORTED]: [],
      [TIndirectTaxTransactionRegion.ALL_REGIONS]: ALL,
      [TIndirectTaxTransactionRegion.UNITED_STATES]: SALES_SIDE, // hide purchases
      [TIndirectTaxTransactionRegion.EUROPEAN_UNION]: ALL,
      [TIndirectTaxTransactionRegion.UNITED_KINGDOM]: ALL,
      [TIndirectTaxTransactionRegion.CANADA]: ALL,
    };
    return map[region];
  }

  /**
   * Build the five summary cards (transactions, gross sales, tax collected,
   * purchases, tax paid) for the aggregate dashboard, in the shape DataTable's
   * `TableSummaryConfig.items` expects.
   *
   * The `currency` argument is what the monetary values are reported in —
   * pass `aggregates.currency` from the response (server-authoritative),
   * falling back to the region-derived currency while the request is in
   * flight. Currency is embedded in each money label like "Gross sales (USD)"
   * so the table widget doesn't need to render symbols separately.
   *
   * Returns an empty array when `aggregates` is undefined so the caller can
   * render a skeleton state.
   */
  static buildAggregateSummaryItems(
    aggregates: TIndirectTaxTransactionAggregates | undefined,
    currency: string,
    region?: TIndirectTaxTransactionRegion
  ): { label: string; value: string | number }[] {
    if (!aggregates) {
      return [];
    }
    const transactions = {
      label: 'Transactions',
      value: formatNumberWithUniqueCommaPattern(aggregates.totalTransactions),
    };
    const grossSales = {
      label: `Gross sales (${currency})`,
      value: this.formatCurrency(aggregates.totalSales, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    };
    const grossPurchases = {
      label: `Gross purchases (${currency})`,
      value: this.formatCurrency(aggregates.totalPurchases, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    };

    // Aggregate tiles differ per region
    // EU and UK report VAT: gross sales/purchases plus output/input VAT.
    if (
      region === TIndirectTaxTransactionRegion.EUROPEAN_UNION ||
      region === TIndirectTaxTransactionRegion.UNITED_KINGDOM
    ) {
      return [
        transactions,
        grossSales,
        {
          label: `Output VAT (${currency})`,
          value: this.formatCurrency(aggregates.totalTaxCollected, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }),
        },
        grossPurchases,
        {
          label: `Input VAT (${currency})`,
          value: this.formatCurrency(aggregates.totalTaxPaid, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }),
        },
      ];
    }

    // US and Canada are sales-tax: tax collected and the total amount.
    if (
      region === TIndirectTaxTransactionRegion.UNITED_STATES ||
      region === TIndirectTaxTransactionRegion.CANADA
    ) {
      return [
        transactions,
        grossSales,
        {
          label: `Tax collected (${currency})`,
          value: this.formatCurrency(aggregates.totalTaxCollected, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }),
        },
      ];
    }

    // All regions (mixed): transaction count with gross sales and purchases.
    return [transactions, grossSales, grossPurchases];
  }

  // Filtering is now server-side via API params — no client-side applyFilters needed

  static filterBySearch(
    transactions: Readonly<TIndirectTaxTransaction[]>,
    query: string
  ): TIndirectTaxTransaction[] {
    if (!query.trim()) return [...transactions];
    const lower = query.toLowerCase();
    return transactions.filter(
      (txn) =>
        txn.id.toLowerCase().includes(lower) ||
        txn.source.toLowerCase().includes(lower) ||
        (txn.customer?.name?.toLowerCase().includes(lower) ?? false) ||
        (txn.customer?.email?.toLowerCase().includes(lower) ?? false) ||
        txn.jurisdictionName.toLowerCase().includes(lower) ||
        txn.countryOrTradeBloc.toLowerCase().includes(lower) ||
        (txn.invoiceNumber?.toLowerCase().includes(lower) ?? false)
    );
  }

  static getTaxBasisName(taxBasis: TIndirectTaxTaxBasis): string {
    const map: Record<TIndirectTaxTaxBasis, string> = {
      [TIndirectTaxTaxBasis.TAX_EXCLUSIVE]: 'Tax-exclusive',
      [TIndirectTaxTaxBasis.TAX_INCLUSIVE]: 'Tax-inclusive',
    };
    return map[taxBasis];
  }

  static getTaxColumnLabel(region: TIndirectTaxTransactionRegion): string {
    if (
      region === TIndirectTaxTransactionRegion.EUROPEAN_UNION ||
      region === TIndirectTaxTransactionRegion.UNITED_KINGDOM
    ) {
      return 'VAT';
    }
    return 'Tax collected';
  }

  static shouldShowSupplyType(region: TIndirectTaxTransactionRegion): boolean {
    return (
      region === TIndirectTaxTransactionRegion.EUROPEAN_UNION ||
      region === TIndirectTaxTransactionRegion.UNITED_KINGDOM
    );
  }

  static shouldShowConversionRate(
    region: TIndirectTaxTransactionRegion,
    transactionCurrency: string,
    filingCurrency: string
  ): boolean {
    const isEuOrUk =
      region === TIndirectTaxTransactionRegion.EUROPEAN_UNION ||
      region === TIndirectTaxTransactionRegion.UNITED_KINGDOM;
    return isEuOrUk && transactionCurrency !== filingCurrency;
  }

  static getRegionFromCountryOrTradeBloc(
    countryOrTradeBloc: string
  ): TIndirectTaxTransactionRegion {
    const map: Record<string, TIndirectTaxTransactionRegion> = {
      'United States': TIndirectTaxTransactionRegion.UNITED_STATES,
      'European Union': TIndirectTaxTransactionRegion.EUROPEAN_UNION,
      'United Kingdom': TIndirectTaxTransactionRegion.UNITED_KINGDOM,
      Canada: TIndirectTaxTransactionRegion.CANADA,
    };
    return map[countryOrTradeBloc] ?? TIndirectTaxTransactionRegion.ALL_REGIONS;
  }

  private static readonly TAX_BREAKDOWN_LABELS: Record<
    TIndirectTaxTransactionRegion,
    string
  > = {
    [TIndirectTaxTransactionRegion.ASIA_PACIFIC]: 'Tax breakdown',
    [TIndirectTaxTransactionRegion.MIDDLE_EAST]: 'Tax breakdown',
    [TIndirectTaxTransactionRegion.LATIN_AMERICA]: 'Tax breakdown',
    [TIndirectTaxTransactionRegion.UNSUPPORTED]: 'Tax breakdown',
    [TIndirectTaxTransactionRegion.ALL_REGIONS]: 'Tax breakdown',
    [TIndirectTaxTransactionRegion.UNITED_STATES]: 'Tax breakdown',
    [TIndirectTaxTransactionRegion.EUROPEAN_UNION]: 'VAT breakdown',
    [TIndirectTaxTransactionRegion.UNITED_KINGDOM]: 'VAT breakdown',
    [TIndirectTaxTransactionRegion.CANADA]: 'Tax breakdown',
  };

  static getTaxBreakdownLabel(region: TIndirectTaxTransactionRegion): string {
    return IndirectTaxTransactionHelper.TAX_BREAKDOWN_LABELS[region];
  }

  private static readonly TAX_BREAKDOWN_SUPPORTED_DESTINATION_COUNTRIES = [
    'US',
    'CA',
  ];

  static isTaxBreakdownSupportedDestination(countryCode?: string): boolean {
    if (!countryCode) {
      return true;
    }
    return IndirectTaxTransactionHelper.TAX_BREAKDOWN_SUPPORTED_DESTINATION_COUNTRIES.includes(
      countryCode
    );
  }

  private static readonly LINE_ITEM_COLUMN_LABELS: Record<
    TIndirectTaxTransactionRegion,
    LineItemColumnLabels
  > = {
    [TIndirectTaxTransactionRegion.ASIA_PACIFIC]: {
      taxLabel: 'Tax',
      totalLabel: 'Line total',
    },
    [TIndirectTaxTransactionRegion.MIDDLE_EAST]: {
      taxLabel: 'Tax',
      totalLabel: 'Line total',
    },
    [TIndirectTaxTransactionRegion.LATIN_AMERICA]: {
      taxLabel: 'Tax',
      totalLabel: 'Line total',
    },
    [TIndirectTaxTransactionRegion.UNSUPPORTED]: {
      taxLabel: 'Tax',
      totalLabel: 'Line total',
    },
    [TIndirectTaxTransactionRegion.ALL_REGIONS]: {
      taxLabel: 'Tax',
      totalLabel: 'Line total',
    },
    [TIndirectTaxTransactionRegion.UNITED_STATES]: {
      taxLabel: 'Tax amount',
      totalLabel: 'Line total',
    },
    [TIndirectTaxTransactionRegion.EUROPEAN_UNION]: {
      taxLabel: 'VAT',
      totalLabel: 'Line total',
    },
    [TIndirectTaxTransactionRegion.UNITED_KINGDOM]: {
      taxLabel: 'VAT',
      totalLabel: 'Line total',
    },
    [TIndirectTaxTransactionRegion.CANADA]: {
      taxLabel: 'Tax amount',
      totalLabel: 'Line total',
    },
  };

  static getLineItemColumnLabels(
    region: TIndirectTaxTransactionRegion
  ): LineItemColumnLabels {
    return IndirectTaxTransactionHelper.LINE_ITEM_COLUMN_LABELS[region];
  }

  static formatCurrencyWithSymbol(currency: string, label: string): string {
    const symbol = IndirectTaxTransactionHelper.getCurrencySymbol(currency);
    return `${label} (${symbol})`;
  }

  static readonly MARKETPLACE_NAME_MAPPING: Record<string, string> = {
    AMAZON: 'Amazon',
  };

  static readonly ACTION_MODAL_CONFIG: Record<
    TIndirectTaxTransactionStatus,
    {
      title: string;
      description: string;
      buttonText: string;
      buttonVariant: 'primary' | 'destructive-primary';
    }
  > = {
    [TIndirectTaxTransactionStatus.VOIDED]: {
      title: 'Mark transaction as void?',
      description:
        'This action will mark the transaction as void. Once void, this transaction will not be included in reporting or filings.',
      buttonText: 'Mark as void',
      buttonVariant: 'destructive-primary',
    },
    [TIndirectTaxTransactionStatus.COMMITTED]: {
      title: 'Mark transaction as to be filed?',
      description:
        'This action will mark the transaction as to be filed. Once marked, this transaction will be included in reporting and filings.',
      buttonText: 'Mark as to be filed',
      buttonVariant: 'primary',
    },
    [TIndirectTaxTransactionStatus.LOCKED]: {
      title: 'Mark transaction as locked?',
      description:
        'This action will lock the transaction. Once locked, this transaction cannot be modified.',
      buttonText: 'Mark as locked',
      buttonVariant: 'primary',
    },
    [TIndirectTaxTransactionStatus.UNCOMMITTED]: {
      title: '',
      description: '',
      buttonText: '',
      buttonVariant: 'primary',
    },
  };

  static parseDateFilterValue(raw: string): {
    fromDate?: string;
    toDate?: string;
  } {
    if (raw.startsWith('custom:')) {
      const [from, to] = raw.replace('custom:', '').split(',');
      return { fromDate: from, toDate: to };
    }

    if (raw.startsWith('preset:')) {
      const code = raw.replace('preset:', '');
      const { from_date, to_date } = calculateDateRange(code);
      return { fromDate: from_date, toDate: to_date };
    }

    return {};
  }

  static buildLineItemTaxLogicRows(
    taxLogic: TIndirectTaxLineItemTaxLogic,
    handlers: TIndirectTaxLineItemTaxLogicHandlers
  ): TIndirectTaxLineItemTaxLogicRow[] {
    const rows: TIndirectTaxLineItemTaxLogicRow[] = [
      {
        question: 'Are you calculating tax here?',
        answer: taxLogic.taxCalculationEnabled,
        linkLabel: 'View registration',
        onView:
          taxLogic.registrationId && handlers.onViewRegistration
            ? () =>
                handlers.onViewRegistration?.(taxLogic.registrationId as string)
            : undefined,
      },
      {
        question: 'Is your customer taxable?',
        answer: taxLogic.customerTaxable,
        linkLabel: 'View customer',
        onView:
          taxLogic.customerId && handlers.onViewCustomer
            ? () => handlers.onViewCustomer?.(taxLogic.customerId as string)
            : undefined,
      },
      {
        question: 'Is your product taxable?',
        answer: taxLogic.productTaxable,
        linkLabel: 'View product',
        onView:
          taxLogic.productId && handlers.onViewProduct
            ? () => handlers.onViewProduct?.(taxLogic.productId as string)
            : undefined,
      },
    ];

    const firstFailedIndex = rows.findIndex((row) => !row.answer);

    return firstFailedIndex === -1 ? rows : rows.slice(0, firstFailedIndex + 1);
  }
}
